import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { tenantSelector } from '../selector/authenticationSelector';
import { isSelfServiceTenant } from '../common/tenantStore';
import {
    useGetAccountQuery,
    useGetTenantInvoiceInfoQuery,
} from '../api/account/accountApiSlice';
import { useIsMaster } from '../common/useIsMaster';
import { useViewedTenantAccount } from './useViewedTenantAccount';
import { mayIssueInvoiceFromKolula } from '../pages/services/serviceConfigurationRules';
import { InvoiceConfig } from '../tenants/config';
import {
    TenantBank,
    TenantBranding,
    TenantLegalNotice,
    TenantOrganizationType,
} from '../api/account/accountApiTypes';
import { config as neulandBundledConfig } from '../tenants/neuland/config';

// The company/legal/bank fields an InvoiceConfig is built from. Both the
// owner's own getAccount tenant and the master-only invoice-info endpoint
// expose exactly these, so one mapper covers both sources.
interface InvoiceSource {
    organizationName: string;
    country: string;
    legalNotice: TenantLegalNotice | null;
    bank: TenantBank | null;
    branding: TenantBranding | null;
}

// Invoice-only extras of tenants migrated from the legacy store (#1419):
// per-use-case tax/bundling, default note and imprint lines have no home in
// the account settings yet, so they are kept from the bundled tenant config
// and merged UNDER the account-derived fields (account data always wins).
// Retire an entry once these fields become tenant-editable account settings.
const MIGRATED_TENANT_INVOICE_EXTRAS: Record<string, Partial<InvoiceConfig>> = {
    neuland: {
        companyNameLines: neulandBundledConfig.invoiceConfig?.companyNameLines,
        supervisoryBoardChair:
            neulandBundledConfig.invoiceConfig?.supervisoryBoardChair,
        defaultInvoiceNote:
            neulandBundledConfig.invoiceConfig?.defaultInvoiceNote,
        useCaseSettings: neulandBundledConfig.invoiceConfig?.useCaseSettings,
    },
};

const mapToInvoiceConfig = (src: InvoiceSource): InvoiceConfig => {
    const ln = src.legalNotice;
    const bank = src.bank;
    // Imprint register line, e.g. "Amtsgericht München HRB 12345".
    const companyRegistration = ln?.hrb
        ? [ln.hrbCourt, ln.hrb].filter(Boolean).join(' ')
        : undefined;
    return {
        companyName: src.organizationName ?? '',
        companyAddress: ln?.street ?? '',
        companyZip: ln?.postalCode ?? '',
        companyCity: ln?.city ?? '',
        // ISO code; the PDF localizes it via localizeCountry (e.g. DE → Deutschland).
        companyCountry: src.country ?? 'DE',
        // VAT id preferred, else the Steuernummer — the same choice as the
        // server's `sellerFromTenant` (new-backend/admin/src/lib/invoicing/parties.ts).
        taxId: ln?.vatId || ln?.taxNumber || undefined,
        // Labels it: a Steuernummer printed as "USt-IdNr." misstates what it is.
        // The server's `taxRegistrationLine` (pdfkitRenderer.ts) labels the same way.
        taxIdKind: ln?.vatId ? 'vat_id' : 'tax_number',
        bankName: bank?.bankName ?? '',
        iban: bank?.iban ?? '',
        bic: bank?.bic ?? '',
        accountHolder: bank?.accountHolder ?? undefined,
        managingDirectors: ln?.managingDirector ?? undefined,
        companyRegistration,
        // Uploaded branding logo → printed on the invoice instead of the
        // kolula fallback the self-service TenantConfig carries.
        logoUrl: src.branding?.logoUrl ?? undefined,
    };
};

// Account-derived fields win; bundled extras only fill the gaps the account
// settings can't express yet (see MIGRATED_TENANT_INVOICE_EXTRAS).
const buildInvoiceConfig = (
    src: InvoiceSource,
    slug: string,
    legalForm: TenantOrganizationType | undefined,
): InvoiceConfig => ({
    ...MIGRATED_TENANT_INVOICE_EXTRAS[slug],
    ...mapToInvoiceConfig(src),
    legalForm,
});

/**
 * Resolves the invoice company/bank config for the selected tenant.
 *
 * - Legacy tenants ship a hardcoded `invoiceConfig` in their bundled config
 *   (`src/tenants/<slug>/config.ts`) — used as-is.
 * - Self-service tenants have no bundled config; the same data lives in their
 *   account settings (organization name, legal notice, bank). It's derived:
 *     - OWNER: from `GET /admin/account` (resolves to their own tenant).
 *     - MASTER: from `GET /admin/tenants/:slug/invoice-info` (getAccount would
 *       return the master's OWN tenant, not the viewed one).
 *
 * Returns undefined when no source resolves — e.g. a legacy tenant without an
 * `invoiceConfig`, or before the relevant query has loaded. Validation of the
 * resulting fields (required company/bank data) stays with the invoice flow,
 * so a partially-filled tenant still sees the button and gets guided errors.
 *
 * It also returns undefined for a tenant whose `invoiceIssuer` is `external`
 * (`mayIssueInvoiceFromKolula`) — their own ERP owns the invoice and its
 * number, which is exactly what the Services form promises them. Gating here
 * rather than at the two booking pages puts the answer where both the button
 * and the generation path already read it, so the two cannot disagree about
 * whether an invoice may be produced.
 */
export const useInvoiceConfig = (): InvoiceConfig | undefined => {
    const bundled = useSelector(
        (s: RootState) => s.authentication.config?.invoiceConfig,
    );
    const selectedTenant = useSelector(tenantSelector);
    const isMaster = useIsMaster();
    const isSelfService = isSelfServiceTenant(selectedTenant);

    // Owner path — getAccount resolves to the caller's own tenant.
    const { data: account } = useGetAccountQuery(undefined, {
        skip: !isSelfService || isMaster || !!bundled,
    });
    // Master path — fetch the viewed tenant by slug (master-gated endpoint).
    const { data: masterInfo } = useGetTenantInvoiceInfoQuery(selectedTenant as string, {
        skip: !isSelfService || !isMaster || !!bundled || !selectedTenant,
    });

    // WHO issues this tenant's invoices (ADR-0012). Read through the shared
    // resolver rather than a bare `useGetAccountQuery()`, which would answer
    // with the CALLER's own tenant and let a master hide — or show — the
    // invoice action by their own setting instead of the viewed tenant's
    // (#1334). The owner path hits the same cached `getAccount` entry this
    // hook already subscribes to, so it costs no extra request; the Stripe
    // status is opted out of because nothing here shows it.
    const { accountTenant } = useViewedTenantAccount({ withPaymentIntegration: false });
    const mayIssue = mayIssueInvoiceFromKolula(accountTenant?.invoiceIssuer);
    // The legal form decides the footer's Pflichtangaben (#3430). Read from
    // the same tenant-in-view, and only when it IS the selected tenant, so a
    // lagging read cannot lend another tenant's form. Not loaded yet means
    // undefined, which prints the lines from before the form was considered.
    const legalForm = accountTenant && accountTenant.slug === selectedTenant
        ? accountTenant.organizationType
        : undefined;

    return useMemo(() => {
        // Before every other case: a tenant who invoices from their own system
        // gets no invoice config at all, bundled extras included.
        if (!mayIssue) return undefined;
        // A legacy store is Mode C by construction (PO, 2026-09-29): kolula
        // cannot be its issuer, and its `invoiceIssuer` never reaches this
        // screen — the account reads above are skipped for legacy tenants
        // (`useViewedTenantAccount`), own login and master view alike. So the
        // stored `external` would be a label nothing reads; this is where the
        // decision takes effect. Without it the one legacy store with a
        // bundled invoice config kept the old client-side PDF.
        if (!isSelfService) return undefined;
        if (bundled) return { ...bundled, legalForm };
        if (!isSelfService || !selectedTenant) return undefined;
        if (isMaster) {
            return masterInfo
                ? buildInvoiceConfig(masterInfo, selectedTenant, legalForm)
                : undefined;
        }
        if (!account || !('tenant' in account)) return undefined;
        const tenant = account.tenant;
        // Guard: only the owner's own account matches the selected tenant. If a
        // mismatch ever surfaces, bail rather than put the wrong company on the
        // invoice.
        if (tenant.slug !== selectedTenant) return undefined;
        return buildInvoiceConfig(tenant, selectedTenant, tenant.organizationType);
    }, [mayIssue, legalForm, bundled, isSelfService, isMaster, masterInfo, account, selectedTenant]);
};
